// API 基础配置
import { API_BASE } from './apiBase'

const API_BASE_URL = API_BASE

// 导入 axios（使用 AppKitchen 的实例）
import axios, { type AxiosRequestConfig } from 'axios'
import { setTokenHeader } from 'librechat-data-provider'
import { refreshAccessToken } from './tokenRefresh'

// ⚠️ 不设全局 axios.defaults.baseURL：librechat-data-provider 的 endpoints（如
// /api/auth/refresh）自带 /api 前缀，假设 baseURL 为空。若设 '/api' 会拼成
// /api/api/auth/refresh → 404 → librechat interceptor 跳登录（曾导致频繁退出）。
// custom-ui 的 apiClient 改用绝对 URL（API_BASE_URL + endpoint），不依赖 baseURL。
// 同源请求带 cookie（refresh 依赖 httpOnly refreshToken cookie）
axios.defaults.withCredentials = true

// ⚠️ 双 axios 事实：custom-ui（node_modules/axios@1.19）与经 vite alias 引入的
// AppKitchen data-provider 源码（解析到 AppKitchen/node_modules/axios@1.18）是两个
// 独立实例，构建产物里有两份 axios。后者在模块加载时给【它自己的】实例挂 LibreChat
// 原生客户端的认证拦截器（401→refresh→失败 redirectToLoginOnce 硬跳 /login 且不清
// token，叠加 LoginPage 的已登录转发会把用户弹回首页）。目前 custom-ui 没有任何请求
// 走那个实例（唯一消费者 categories.adapter 的路径缺 /api 前缀，实际拿到 SPA HTML），
// 拦截器处于休眠——但它是上一次「频繁退出」事故的同族机制（见上方针入口注释）。
// 此处清除 custom-ui 实例的拦截器是策略性防御：
//   ① 禁止在 custom-ui 全局 axios 上注册认证类拦截器——401 统一走 tokenRefresh.ts
//      单管线（轮换竞态恢复也在那里）；
//   ② 防未来两份 axios 合流（dedupe/版本对齐）时休眠拦截器复活。
// 在本模块求值时执行一次，先于任何应用代码注册；若未来要加自己的拦截器，必须在其后。
if (typeof window !== 'undefined') {
  axios.interceptors.request.clear()
  axios.interceptors.response.clear()
}

// 获取当前 token 的辅助函数
// localStorage 是 token 真相源（tokenRefresh 成功 / 多标签竞态赢家即时写回）；
// this.token 缓存仅兜底并随读刷新——否则 15 分钟 token 过期后 refresh 成功了，
// 重试仍带过期缓存 token，必然再 401（401→refresh→重试死循环）。
const getCurrentToken = (fallback: string | null): string | null => {
  const token = localStorage.getItem('token') ?? fallback
  return token
}

/** 懒加载 token -- 在首次需要时从 localStorage 读取 */
let _initTokenCalled = false

const initToken = () => {
  if (_initTokenCalled) return
  _initTokenCalled = true
  const token = localStorage.getItem('token')
  if (token) {
    setTokenHeader(token)
    if (import.meta.env.DEV) console.log('[apiClient] 初始化 token:', token.slice(0, 20) + '...')
  } else {
    console.warn('[apiClient] 初始化时 localStorage 无 token，将懒加载')
  }
}

// 初始化 token
initToken()

// 监听 token 更新事件（来自 AppKitchen 的 tokenUpdated 事件）
if (typeof window !== 'undefined') {
  window.addEventListener('tokenUpdated', ((event: CustomEvent) => {
    const token = event.detail
    if (import.meta.env.DEV) console.log('[apiClient] token 更新:', token.slice(0, 20) + '...')
    setTokenHeader(token)
  }) as EventListener)
}

// HTTP 客户端 - 使用 axios
class HttpClient {
  private token: string | null = null

  /** 设置 token（供 auth.service 使用） */
  setToken(token: string | null): void {
    this.token = token
    if (import.meta.env.DEV) console.log('[apiClient] setToken 被调用:', token ? '已设置' : '已清除')

    // 同时更新 axios 默认 header
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }

  private async request<T>(
    endpoint: string,
    options: Partial<AxiosRequestConfig> = {}
  ): Promise<T> {
    const url = endpoint.startsWith('http') || endpoint.startsWith(API_BASE_URL)
      ? endpoint
      : API_BASE_URL + endpoint

    // token 以 localStorage 为真相源（见 getCurrentToken 注释），缓存随读刷新
    const token = getCurrentToken(this.token)
    if (token) {
      this.token = token
    }

    // 详细日志
    if (!token) {
      console.error('[apiClient] 请求不包含 token!', { url, endpoint })
    } else {
      if (import.meta.env.DEV) console.log('[apiClient] 使用 token:', token.slice(0, 20) + '...')
    }

    if (token && !options.headers?.['Authorization']) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    }

    try {
      const response = await axios({
        url,
        ...options,
      })

      return response.data
    } catch (axiosError: unknown) {
      if (axiosError && typeof axiosError === 'object' && 'response' in axiosError) {
        const axiosResponse = axiosError.response as { status?: number; statusText?: string; data?: unknown }
        const { status, statusText, data: responseData } = axiosResponse

        // 401 时自动刷新 token 并重试一次（refreshAccessToken 统一处理 + 防并发，失败自动跳登录）
        if (status === 401 && !(options as any)._retried) {
          const newToken = await refreshAccessToken()
          if (newToken) {
            // 重试必须换新 Authorization——原 options.headers 里缓存的是触发 401 的旧 token，
            // request() 见已有头会跳过附加，导致重试仍发旧 token 必然再 401
            const retryHeaders = {
              ...(options.headers as Record<string, string> | undefined),
              Authorization: `Bearer ${newToken}`,
            }
            return this.request<T>(endpoint, {
              ...options,
              headers: retryHeaders,
              _retried: true,
            } as any)
          }
          // refresh 失败：refreshAccessToken 已跳登录，抛错中断
          throw new Error('登录已过期，请重新登录')
        }

        // ⚠️ 重要：优先使用后端返回的错误信息（message 是给用户看的中文提示，优先于
        // error 错误码——如 402 QUOTA_EXHAUSTED/SUBAGENT_LOW_BALANCE 的 message 引导文案）
        const backendError = (responseData as { error?: string; message?: string })?.message || (responseData as { error?: string; message?: string })?.error

        console.error(`[apiClient] 请求失败:`, {
          url,
          status,
          statusText,
          backendError,
          responseData
        })

        // 如果后端返回了具体错误，使用它；否则使用 HTTP 状态
        if (backendError) {
          // 挂上 response（含 status），供调用方按状态码分支（如 Co-op 409 抢答冲突）
          const be = new Error(backendError) as Error & { response?: unknown }
          be.response = axiosError.response
          throw be
        }

        // 构造更详细的错误
        const error = new Error(`HTTP ${status}: ${statusText}`) as Error & { response?: unknown }
        error.response = axiosError.response
        throw error
      }
      console.error('[apiClient] 请求异常:', axiosError)
      throw axiosError
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: unknown, options: Record<string, unknown> = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      data,
      headers: { 'Content-Type': 'application/json' },
      ...options,
    })
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      data,
      headers: { 'Content-Type': 'application/json' },
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      data,
      headers: { 'Content-Type': 'application/json' },
    })
  }

  async delete<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      data,
      headers: data ? { 'Content-Type': 'application/json' } : undefined,
    })
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', data: formData })
  }
}

// 创建客户端实例
export const apiClient = new HttpClient()

// API 端点
export const API_ENDPOINTS = {
  // AI 小伙伴
  assistants: '/assistants',
  assistant: (id: string) => `/assistants/${id}`,
  assistantChat: (id: string) => `/assistants/${id}/chat`,

  // 分类
  categories: '/categories',

  // 用户
  user: '/user',
  userFavorites: '/user/favorites',
  userHistory: '/user/history',

  // 社区
  communityPosts: '/community/posts',
  communityPost: (id: string) => `/community/posts/${id}`,
  communityComments: (id: string) => `/community/posts/${id}/comments`,
} as const
